// Why an unpublished event is stuck, and whether the rescue lane can fix it.
// Shared by scripts/seeding/list-review-queue.ts (the rescue's queue) and the
// requestRescueOnUnpublishedCreate Cloud Function, so both agree on what is
// worth a rescue run.

/** A reason that a browser render would plausibly fix. */
const BROWSER_REASON = /browser|bot.?block|cloudflare|captcha|403|blocked by/i

/**
 * `dead` is terminal for the pipeline: the source is confirmed gone, so no
 * re-fetch can ever change the answer. The rescue lane skips this bucket — it
 * used to spend unlocker credits re-proving the same 404 every run (#397). Only
 * an admin closes it, by deleting the event or attaching a new source.
 */
export type ReviewBucket = 'browser' | 'cover' | 'dead' | 'other'

export function reviewBucketOf(reasons: string[]): ReviewBucket {
  if (reasons.includes('source-dead')) return 'dead'
  if (reasons.some(r => BROWSER_REASON.test(r))) return 'browser'
  if (reasons.includes('no-cover')) return 'cover'
  return 'other'
}

interface RescuableEvent {
  urls?: { tickets?: string; instagram?: string; website?: string }
  ticketUrl?: string
}

/** Best source URL to re-render, '' when the event has none. */
export function rescueSourceUrl(event: RescuableEvent): string {
  const urls = event.urls ?? {}
  return urls.tickets || urls.instagram || urls.website || event.ticketUrl || ''
}

/** Only these buckets, with a source to re-fetch, can a rescue run fix. */
export function isRescuable(reasons: string[], event: RescuableEvent): boolean {
  const bucket = reviewBucketOf(reasons)
  return (bucket === 'browser' || bucket === 'cover') && rescueSourceUrl(event) !== ''
}
